import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../db/firebase";
import { useAuth } from "../../context/AuthContext";
import {
  HiOutlineUsers,
  HiOutlineCalendarDays,
  HiOutlineMusicalNote,
  HiOutlineBuildingLibrary,
  HiOutlineArrowRight,
} from "react-icons/hi2";
import { motion } from "framer-motion";
import StatCard from "./Components/StatCard";
import { useTranslation } from "react-i18next";
import TopSongs from "./Events/TopSongs";
import UserEventsChart from "./Events/UserEventChart";

const FullPageSkeleton: React.FC = () => (
  <div className="flex flex-col space-y-6 py-4 animate-pulse">
    <div className="space-y-2">
      <div className="h-4 w-28 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-8 w-56 rounded-lg bg-gray-200 dark:bg-gray-700" />
    </div>
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {[1, 2, 3, 4].map((i) => (
        <div
          key={i}
          className="h-28 rounded-2xl bg-gray-200 dark:bg-gray-700"
        />
      ))}
    </div>
    <div className="h-72 rounded-2xl bg-gray-200 dark:bg-gray-700" />
    <div className="h-56 rounded-2xl bg-gray-200 dark:bg-gray-700" />
  </div>
);

const AdminDashboard: React.FC = () => {
  const { user, isAdmin, organizationId, profile } = useAuth();
  const [approvedUsersCount, setApprovedUsersCount] = useState(0);
  const [ministriesCount, setMinistriesCount] = useState(0);
  const [eventsCount, setEventsCount] = useState(0);
  const [songsCount, setSongsCount] = useState(0);
  const [error, setError] = useState("");
  const [userName, setUserName] = useState("");
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      if (!user || !organizationId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError("");

      try {
        const orgId = organizationId;

        const [usersSnap, ministriesSnap, eventsSnap, songsSnap, orgDoc] =
          await Promise.all([
            getDocs(
              query(collection(db, "users"), where("organizationId", "==", orgId)),
            ),
            getDocs(
              query(
                collection(db, "ministries"),
                where("organizationId", "==", orgId),
              ),
            ),
            getDocs(
              query(
                collection(db, "events"),
                where("organizationId", "==", orgId),
              ),
            ),
            getDocs(collection(db, "songs")),
            getDoc(doc(db, "organizations", orgId)),
          ]);

        if (cancelled) return;

        setApprovedUsersCount(
          usersSnap.docs.filter((d) => d.data().approved === true).length,
        );
        setMinistriesCount(ministriesSnap.size);
        setEventsCount(eventsSnap.size);
        setSongsCount(songsSnap.size);

        const fromProfile = [profile?.firstName, profile?.lastName]
          .filter(Boolean)
          .join(" ")
          .trim();
        if (fromProfile) {
          setUserName(fromProfile);
        } else if (orgDoc.exists()) {
          setUserName(orgDoc.data().orgName || "");
        } else if (isAdmin) {
          const userDoc = await getDoc(doc(db, "users", user.uid));
          if (!cancelled && userDoc.exists()) {
            const data = userDoc.data();
            setUserName(
              [data.firstName, data.lastName].filter(Boolean).join(" ").trim(),
            );
          }
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(t("errorFetchingDashboard") || "Could not load dashboard.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchData();
    return () => {
      cancelled = true;
    };
    // profile/t intentionally omitted — avoid refetch loops that re-show skeleton
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, organizationId, isAdmin]);

  if (loading) {
    return <FullPageSkeleton />;
  }

  const quickLinks = [
    {
      to: "/users",
      title: t("users"),
      subtitle: t("totalMembers"),
      icon: HiOutlineUsers,
    },
    {
      to: "/events",
      title: t("events"),
      subtitle: t("totalEvents"),
      icon: HiOutlineCalendarDays,
    },
    {
      to: "/songs",
      title: t("songs"),
      subtitle: t("totalSongs"),
      icon: HiOutlineMusicalNote,
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: "easeOut" }}
      className="flex flex-col min-h-0 space-y-6 sm:space-y-8"
    >
      <div>
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#4c2bd4] dark:text-[#c4b5fd] text-xs font-medium mb-3 backdrop-blur-sm">
          {t("dashboard")}
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white tracking-tight">
          {t("welcome")}
          {userName ? `, ${userName}` : ""}
        </h1>
        <p className="mt-1.5 text-sm text-gray-500 dark:text-gray-400">
          {t("dashboardSubtitle") ||
            "Overview of your organization, events, and music"}
        </p>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={<HiOutlineUsers className="w-6 h-6" />}
          accentColor="#5932EA"
          value={approvedUsersCount}
          title={t("totalMembers")}
        />
        <StatCard
          icon={<HiOutlineBuildingLibrary className="w-6 h-6" />}
          accentColor="#7C3AED"
          value={ministriesCount}
          title={t("totalMinistries")}
        />
        <StatCard
          icon={<HiOutlineCalendarDays className="w-6 h-6" />}
          accentColor="#059669"
          value={eventsCount}
          title={t("totalEvents")}
        />
        <StatCard
          icon={<HiOutlineMusicalNote className="w-6 h-6" />}
          accentColor="#DB2777"
          value={songsCount}
          title={t("totalSongs")}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {quickLinks.map(({ to, title, subtitle, icon: Icon }) => (
          <Link
            key={to}
            to={to}
            className="group flex items-center gap-3.5 p-4 rounded-2xl border border-gray-200/70 dark:border-gray-700/70 bg-white/60 dark:bg-gray-800/40 backdrop-blur-sm hover:border-[#5932EA]/30 hover:bg-[#5932EA]/5 dark:hover:bg-[#5932EA]/10 transition-all"
          >
            <div className="flex items-center justify-center w-11 h-11 rounded-xl border border-[#5932EA]/25 bg-[#5932EA]/10 text-[#5932EA] shrink-0">
              <Icon className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 dark:text-gray-100">
                {title}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {subtitle}
              </p>
            </div>
            <HiOutlineArrowRight className="w-4 h-4 text-gray-400 group-hover:text-[#5932EA] group-hover:translate-x-0.5 transition-all shrink-0" />
          </Link>
        ))}
      </div>

      <div className="space-y-5 sm:space-y-6">
        <UserEventsChart organizationId={organizationId} />
        <TopSongs />
      </div>

      {error && (
        <p className="text-sm text-red-500 dark:text-red-400">{error}</p>
      )}
    </motion.div>
  );
};

export default AdminDashboard;
